<template>
  <div class="session-quiz">

    <div v-if="isFetching" class="page-loader">
      <c-loader />
    </div>

    <Card v-else-if="!isFetching && errorStatus">
      <ErrorPage :status="errorStatus" :text="errorMessage"/>
    </Card>

    <Card v-else>

      <div class="session-content-block">
        <h4 class="session-content-block__title" style="color: var(--attendee-brand-color);">
          <c-button
            size="sm"
            theme="default"
            @click="goBack()"
          >
            <i class="fa-regular fa-arrow-left" style="color: var(--attendee-text-primary-color);"></i>
          </c-button>

          {{ title }}
        </h4>
      </div>

      <Notify :theme="mode.tryAgain ? 'danger' : 'primary'" v-if="!mode.failed && !quiz?.attendee?.completed">
        Attempts:
        <strong v-if="quiz?.attempts === 0">Unlimited</strong>
        <strong v-else>{{quiz?.attendee?.attempt}} / {{quiz?.attempts}}</strong>
        <br>
        <strong>{{quiz?.threshold_percent}}% to Pass</strong>
      </Notify>

      <Notify theme="success" v-else-if="quiz?.attendee?.completed && !mode.failed">
        You have pass the quiz with result of <strong>{{quiz?.attendee?.threshold_percent}}% / {{quiz?.threshold_percent}}%</strong>
      </Notify>

      <Notify theme="danger" v-else>
        You have failed the quiz with result of <strong>{{quiz?.attendee?.threshold_percent}}% / {{quiz?.threshold_percent}}%</strong>
      </Notify>

      <br />

      <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
        <form @submit.prevent="submit">
          <evaluation-table-layout :title="quiz.name" :description="quiz.description" :noContentPadding="true">
            <div v-for="(question, index) in quiz.questions"  class="quiz-item-outside">
              <evaluation-item-layout :title="question.question" direction="column">
                <div :class="`is-current-answer ${question?.attendee && question?.attendee?.submitted_answer === question.value ? question?.attendee.is_correct ? 'success' : 'failed' : '' }`">
                  <validation-provider :vid="`answers.${index}.answerKey`" rules="required" v-slot="{errors}">
                    <!--                        :cols="question.answers.length"-->
                    <c-radio-group
                      :id="`quiz-${index}-question-${question.id}`"
                      :name="`quiz_${index}_question_${question.id}`"
                      label-key="value"
                      label-value="key"
                      v-model="question.value"
                      :options="question.answers"
                      :error="errors[0]"
                    />
<!--                      :mediaQuery="[{media: 468, cols: 1},{media: 768, cols: 3},{media: 1000, cols: 4}, {media: 1100, cols: 5}]"-->
                  </validation-provider>
                </div>
              </evaluation-item-layout>
            </div>
          </evaluation-table-layout>
        </form>
      </validation-observer>

      <Notify theme="danger" v-if="submitError" class="quiz-submit-error">
        {{ submitError }}
      </Notify>

      <div class="quiz-buttons">
        <c-button
          v-if="mode.tryAgain"
          type="button"
          theme="warning"
          size="sm"
          icon="fa-solid fa-arrow-rotate-right"
          uppercase
          fill
          @click="tryAgain"
        >Try Again</c-button>

        <c-button
          v-if="mode.continue"
          type="button"
          theme="success"
          size="sm"
          icon="fa-solid fa-paper-plane"
          uppercase
          fill
          @click="next"
        >Continue</c-button>

        <c-button
          v-if="mode.send"
          type="submit"
          theme="primary"
          size="sm"
          icon="fa-solid fa-paper-plane"
          :show-indicator="showIndicator"
          uppercase
          fill
          @click="submit"
        >Submit Your Response</c-button>
      </div>
    </Card>
  </div>
</template>

<script>
import Card from "../../../components/ui/cards/card";
import Notify from "../../../components/ui/Notify";
import EvaluationTableLayout from "../../../components/evaluations/evaluation-table-layout";
import EvaluationItemLayout from "../../../components/evaluations/evaluation-item-layout";
import {mapGetters} from "vuex";
import ErrorPage from "../../error-page";

export default {
  props:['get', 'send', 'redirect', 'type'],
  components: {
    Card,
    Notify,
    EvaluationTableLayout,
    EvaluationItemLayout,
    ErrorPage
  },
  data() {
    return {
      quiz: {
        questions: []
      },
      // showFailedMessage: false,
      isFetching: false,
      showIndicator: false,
      errorStatus: null,
      errorMessage: null,
      submitError: null,
      attendeeId: this.$route.params.attendeeId,
      sessionId: this.$route.params.sessionId,
      title: '',
      mode: {
        continue: false,
        tryAgain: false,
        send: true,
        failed: false,
      },
    }
  },
  mounted() {
    this.fetchQuiz()
  },
  methods: {
    goBack() {
      // For session quiz/evaluation, returning to the previous page can re-open an
      // already-submitted evaluation/quiz and hit a 422. Send the attendee to the
      // sessions list instead.
      if (this.type === 'session') {
        this.$router.push({ name: 'Full Schedule', params: { attendeeId: this.attendeeId } });
        return;
      }

      let page = this.$route.query.back;

      if (!page) {
        page = 'My Certificate';
      }

      this.$router.push({ name: page });
    },

    fetchQuiz() {
      this.isFetching = true;
      this.errorStatus = null;
      this.errorMessage = null;
      this.get()
        .then((res) => {
          this.title = res.data?.data?.title;
          this.quizTransform(res.data?.data?.items);
        })
        .catch((error) => {
          this.errorStatus = error?.response?.status;
          this.errorMessage = error?.response?.data?.flash || null;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    tryAgain(){
      // clear attendee answers and remove disable from radio form
      this.quiz = {
        ...this.quiz,
        questions: this.quiz.questions.map((question) => {
          delete question.attendee;

          return {
            ...question,
            value: null,
            answers: question.answers.map((item)=>{
              return {
                ...item,
                disabled: false,
              }
            })
          }
        })
      }

      // set
      this.mode.tryAgain = false;
      this.mode.send = true;
    },

    next() {

      if (this.type === 'session') {
        if (this.quiz.evaluation_completed) {
          this.goBack();
        } else {
          this.$router.push(this.redirect);
        }
      }

      if (this.type === 'event') {
        // after success evaluation push user to evaluation of that session
        this.$router.push(this.redirect);
      }
    },

    quizTransform(quiz) {
      const disabled = quiz.attendee.attempt !== 0 || quiz.attendee.completed || ( quiz.attempts !== 0 && quiz.attendee.attempt > quiz.attempts);

      // after any send disable all radio form
      this.quiz = {
        ...quiz,
        questions: quiz.questions.map((question) => {
          return {
            ...question,
            value: question.attendee.submitted_answer,
            answers: question.answers.map((item)=>{
              return {
                ...item,
                disabled: disabled,
              }
            })
          }
        })
      }

      // if quiz is completed success
      if (quiz.attendee.completed) {
        this.mode.send = false;
        this.mode.continue = true;
        this.mode.tryAgain = false;

      } else {
        if (quiz.attendee.attempt === 0){
          this.mode.send = true;

          this.mode.tryAgain = false;
          this.mode.continue = false;
          // if you have attempts and or you have infinity attempts
        } else if (quiz.attendee.attempt < quiz.attempts || quiz.attempts === 0){
          // hide all buttons leave only try again button
          this.mode.tryAgain = true;

          this.mode.send = false;

          if (quiz.required_for_ce) {
            this.mode.continue = false;
          } else {
            this.mode.continue = true;
          }
        } else {
          // show failed message
          this.mode.failed = true;

          // hide all buttons
          this.mode.tryAgain = false;
          this.mode.send = false;
          this.mode.continue = false;
        }
      }
    },

    submit() {
      this.$refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        this.showIndicator = true;
        this.submitError = null;

        const data = this.quiz.questions.map((item) => {
          return {
            answerKey: item.answers.find(answer => answer.value === item.value).key,
            questionId: item.id,
          }
        });

        this.send({answers: data})
          .then((res) => {
            if (this.type === 'session') {
              this.$store.dispatch("agendaStore/updateFlatSessionById", {id: res.data?.data?.session?.id, session: res.data?.data?.session});
            }
            if (this.type === 'event') {
              this.$store.commit("agendaStore/updateAgendaButtons", { ...res.data?.data?.buttons });
            }

            this.quizTransform(res.data?.data?.quiz);
          })
          .catch((error) => {
            if (error?.response?.data?.errors) {
              this.$refs.form.setErrors(error.response.data.errors);
            } else {
              // A backend flash message (e.g. a business rule rejection) shown under the form.
              this.submitError = error?.response?.data?.flash || null;
            }
          })
          .finally(() => {
            this.showIndicator = false;
          });

        this.$nextTick(() => {
          this.$refs.form.reset();
        });
      });
    },
  }
}
</script>

<style lang="scss">

.quiz-item-outside{
  &:last-child {
    .evaluation-item {
      border-bottom: none;
    }
  }
}
.session-quiz {


  .app-radio, .app-checkbox {
    //grid-template-columns: auto;
    //justify-items: center;
    width: 100%;
  }

  .is-current-answer {
    &.success {
      .app-radio,
      .app-checkbox {
        input:checked ~ span {
          background-color: #189b18;
          border: 2px solid #189b18;
        }
      }
    }

    &.failed {
      .app-radio,
      .app-checkbox {
        input:checked ~ span {
          background-color: #ff5353;
          border: 2px solid #ff5353;
        }
      }
    }
  }
}

.quiz-submit-error {
  margin: 10px 0;
}

.quiz-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
